import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * /auth/magic?token=... — verifies the magic-link, stores the returned token,
 * applies any pending invite, and navigates to redirect (or /).
 */
export default function MagicLinkVerify() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useContext(AuthContext);
  const [status, setStatus] = useState('verifying'); // verifying | error
  const [message, setMessage] = useState('Signing you in…');
  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    const token = params.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Missing link token.');
      return;
    }

    (async () => {
      try {
        const res = await axios.get(`${API}/api/auth/magic-link/verify`, {
          params: { token },
          withCredentials: true,
        });
        const { user, token: accessToken, invite_code, redirect } = res.data || {};
        if (accessToken) localStorage.setItem('token', accessToken);
        if (user) setUser(user);

        // Auto-apply invite if the link carried one (takes precedence over sessionStorage)
        const inviteToApply = invite_code || sessionStorage.getItem('lk_pending_invite');
        if (inviteToApply) {
          try {
            const r = await axios.post(
              `${API}/api/auth/apply-pending-invite`,
              { code: inviteToApply },
              {
                headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
                withCredentials: true,
              }
            );
            if (r.data?.applied) {
              toast.success(`You joined through ${r.data.inviter_name}`);
            }
          } catch { /* silent */ }
          sessionStorage.removeItem('lk_pending_invite');
        }

        toast.success('Signed in');
        navigate(redirect || '/', { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setStatus('error');
        setMessage(
          typeof detail === 'string'
            ? detail
            : 'This sign-in link is invalid or has expired.'
        );
      }
    })();
  }, [params, navigate, setUser]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ backgroundColor: 'var(--fy-background-primary)' }}
      data-testid="magic-link-verify"
    >
      <div className="w-full max-w-sm text-center">
        {status === 'verifying' ? (
          <>
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[#7A8F5B]" />
            <p className="mt-4" style={{ color: 'var(--fy-text-secondary)' }}>
              {message}
            </p>
          </>
        ) : (
          <>
            <h1
              className="text-2xl font-bold mb-3"
              style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit' }}
            >
              Sign-in link didn’t work
            </h1>
            <p className="mb-6" style={{ color: 'var(--fy-text-secondary)' }}>
              {message}
            </p>
            <button
              onClick={() => navigate('/auth?mode=signin', { replace: true })}
              className="h-12 px-6 rounded-full font-medium"
              style={{ backgroundColor: '#7A8F5B', color: '#FFFFFF' }}
              data-testid="magic-link-retry-signin"
            >
              Sign in another way
            </button>
          </>
        )}
      </div>
    </div>
  );
}
